import React, { useState, useRef, useEffect } from 'react';
import { useOplaes } from '../../providers/OplaesProvider';
import Oplae from './Oplae';
import { cn } from '../../lib/utils';
import { 
  LayoutGrid, GitBranch, ArrowRight, Circle, CheckCircle2, Clock, 
  Zap, Users, Target, ArrowUpRight, Play, Pause, Film, Heart, 
  MessageCircle, Share2, TrendingUp, BarChart2, Instagram, 
  Plus, Settings, Minus, AlertCircle, User, Send,
  Loader2,
} from 'lucide-react';
import TiktokIcon from '../ui/TiktokIcon';
import { motion, AnimatePresence, useAnimation, useMotionValue, useTransform } from 'framer-motion';

const colors = [
  '#7C3AED', // Purple
  '#EC4899', // Pink
  '#3B82F6', // Blue
  '#10B981', // Emerald
  '#F59E0B', // Amber
  '#EF4444', // Red
  '#22C55E', // Green
  '#F43F5E', // Rose
  '#14B8A6', // Teal
  '#78716C', // Stone
];

// Calculate relative dates
const now = new Date();
const today = now.toISOString().split('T')[0];
const dayBeforeYesterday = new Date(now.getTime() - 2*24*60*60*1000).toISOString().split('T')[0];
const yesterday = new Date(now.getTime() - 24*60*60*1000).toISOString().split('T')[0];
const tomorrow = new Date(now.getTime() + 24*60*60*1000).toISOString().split('T')[0];
const dayAfterTomorrow = new Date(now.getTime() + 2*24*60*60*1000).toISOString().split('T')[0];

// Example data structure
const exampleOplaes = {
  active: [
    {
      id: 1,
      title: "Peak Hours Posting",
      color: '#7C3AED', // Purple
      goal: {
        cadence: 'weekly',
        start_date: "2024-03-01T00:00:00",
        videos_count: 2
      },
      engagement_rate: 8.5,
      strategy: "Peak Hours",
      engagement_rate_history: [
        { date: "2024-03-01", value: 3.2 },
        { date: "2024-03-05", value: 4.8 },
        { date: "2024-03-10", value: 6.3 },
        { date: "2024-03-15", value: 8.5 }
      ],
      videos: [
        {
          id: "post_1_1",
          title: "How to Master Social Media Marketing in 2024: Complete Guide",
          colors: ['#9CA3AF'],
          scheduled_date: `${dayBeforeYesterday}T15:00:00`,
          temperature: 0.9, // High - very viral
          stats: {
            comments: 15,
            shares: 892,
            likes: 2459
          },
          strategy: "Biggest fan",
          comments: [
            { author: "Sarah Johnson", comment: "This is exactly what I needed! Your insights on algorithm changes are game-changing 🙌", likes: 245, timestamp: "2h ago" },
            { author: "Mike Chen", comment: "Been implementing these strategies for a week now and already seeing 2x engagement!", likes: 189, timestamp: "3h ago" },
            { author: "Emma Davis", comment: "The section about content batching is pure gold. Sharing with my marketing team!", likes: 156, timestamp: "3h ago" },
            { author: "Alex Thompson", comment: "Finally someone explains the new analytics dashboard properly!", likes: 134, timestamp: "4h ago" },
            { author: "Rachel Kim", comment: "Your tips on reels vs shorts strategy is spot on 🎯", likes: 122, timestamp: "4h ago" },
            { author: "David Park", comment: "This changed my whole content strategy. Thank you!", likes: 167, timestamp: "5h ago" },
            { author: "Lisa Wong", comment: "The engagement formula you shared is brilliant", likes: 198, timestamp: "5h ago" },
            { author: "James Smith", comment: "Taking notes furiously! This is next level stuff 📝", likes: 143, timestamp: "6h ago" },
            { author: "Maria Garcia", comment: "Love how you break down complex concepts", likes: 176, timestamp: "6h ago" },
            { author: "Tom Wilson", comment: "These insights are worth their weight in gold 🏆", likes: 154, timestamp: "7h ago" },
            { author: "Ana Silva", comment: "Already implemented the first 3 tips - amazing results!", likes: 187, timestamp: "7h ago" },
            { author: "John Lee", comment: "This is why you're my go-to for social media strategy", likes: 165, timestamp: "8h ago" },
            { author: "Sophie Brown", comment: "Mind-blown by the cross-platform strategy 🤯", likes: 145, timestamp: "8h ago" },
            { author: "Ryan Taylor", comment: "The best social media guide I've seen this year", likes: 178, timestamp: "9h ago" },
            { author: "Nina Patel", comment: "Your content never disappoints! 👏", likes: 132, timestamp: "9h ago" }
          ]
        },
        {
          id: "post_1_2",
          title: "Advanced Social Media Strategies for 2024",
          colors: ['#9CA3AF'],
          scheduled_date: `${yesterday}T16:00:00`, // Same date as post_1_1
          temperature: 0.5, // Medium - moderately viral
          stats: {
            comments: 8,
            shares: 345,
            likes: 1367
          },
          strategy: "Biggest fan",
          comments: [
            { author: "Chris Martinez", comment: "The advanced targeting techniques are eye-opening!", likes: 167, timestamp: "1h ago" },
            { author: "Olivia White", comment: "Love the deep dive into social listening tools", likes: 189, timestamp: "2h ago" },
            { author: "Kevin Zhang", comment: "The competitive analysis framework is brilliant", likes: 201, timestamp: "3h ago" },
            { author: "Maya Patel", comment: "These advanced metrics changed how I measure success", likes: 145, timestamp: "4h ago" },
            { author: "Ben Cooper", comment: "The section on predictive analytics is gold 🏆", likes: 167, timestamp: "5h ago" },
            { author: "Sarah Johnson", comment: "Your advanced hashtag research method is revolutionary", likes: 134, timestamp: "6h ago" },
            { author: "Mike Chen", comment: "Finally understanding social media attribution properly", likes: 156, timestamp: "7h ago" },
            { author: "Emma Davis", comment: "The advanced automation workflows are saving me hours! 🙌", likes: 178, timestamp: "8h ago" }
          ]
        }
      ]
    },
    {
      id: 2,
      title: "Weekly Content",
      color: '#EC4899', // Pink
      goal: {
        cadence: 'weekly',
        start_date: "2024-02-15T00:00:00",
        videos_count: 5
      },
      engagement_rate: 12.3,
      strategy: null,
      engagement_rate_history: [
        { date: "2024-02-15", value: 4.1 },
        { date: "2024-02-20", value: 6.7 },
        { date: "2024-02-25", value: 8.9 },
        { date: "2024-03-01", value: 10.2 },
        { date: "2024-03-05", value: 11.5 },
        { date: "2024-03-10", value: 11.8 },
        { date: "2024-03-15", value: 12.3 }
      ],
      videos: [
        {
          id: "post_2_1",
          title: "5 Essential Tips for Better Time Management",
          colors: ['#9CA3AF'],
          scheduled_date: `${yesterday}T14:00:00`,
          temperature: 0.1, // Low - not viral
          stats: {
            comments: 3,
            shares: 45,
            likes: 232
          },
          strategy: "Best Comments",
          comments: [
            { author: "Lisa Wong", comment: "The time blocking technique changed my workday completely!", likes: 89, timestamp: "4h ago" },
            { author: "James Smith", comment: "Love how you explained the 2-minute rule", likes: 76, timestamp: "5h ago" },
            { author: "Maria Garcia", comment: "Finally understanding why multitasking doesn't work", likes: 67, timestamp: "6h ago" }
          ]
        },
        {
          id: "post_2_2",
          title: "Productivity Hacks for Remote Work Success",
          colors: ['#EC4899'],
          scheduled_date: `${tomorrow}T15:00:00`,  // Future - scheduled
          temperature: null,
          stats: null,
          strategy: "Biggest fans",
          comments: []
        },
        {
          id: "post_2_3",
          title: "Building a Strong Personal Brand Online",
          colors: ['#EC4899', '#10B981'],
          scheduled_date: `${tomorrow}T16:00:00`, // Future - scheduled, same date as post_2_2
          temperature: null,
          stats: null,
          strategy: "Biggest fans",
          comments: []
        },
        {
          id: "post_2_4",
          title: "Digital Marketing Trends for 2024",
          colors: ['#9CA3AF'],
          scheduled_date: `${yesterday}T15:00:00`,
          temperature: 0.9, // High - very viral
          stats: {
            comments: 12,
            shares: 678,
            likes: 1892
          },
          strategy: "Biggest fans",
          comments: [
            { author: "Tom Wilson", comment: "The AI marketing predictions are spot on! Can't wait to try the new tools", likes: 234, timestamp: "1h ago" },
            { author: "Ana Silva", comment: "Your insights on voice search optimization are revolutionary", likes: 198, timestamp: "2h ago" },
            { author: "John Lee", comment: "The section on zero-party data strategies is gold 🏆", likes: 187, timestamp: "2h ago" },
            { author: "Sophie Brown", comment: "Fascinating take on Web3 marketing applications", likes: 176, timestamp: "3h ago" },
            { author: "Ryan Taylor", comment: "The AR/VR marketing possibilities are mind-blowing 🤯", likes: 165, timestamp: "3h ago" },
            { author: "Nina Patel", comment: "Great breakdown of privacy-first marketing strategies", likes: 154, timestamp: "4h ago" },
            { author: "Chris Martinez", comment: "The predictive analytics tools you mentioned are game-changers", likes: 143, timestamp: "4h ago" },
            { author: "Olivia White", comment: "Already implementing the sustainable marketing practices!", likes: 132, timestamp: "5h ago" },
            { author: "Kevin Zhang", comment: "Your analysis of micro-moment marketing is brilliant", likes: 121, timestamp: "5h ago" },
            { author: "Maya Patel", comment: "The social commerce integration tips are incredibly practical", likes: 110, timestamp: "6h ago" },
            { author: "Ben Cooper", comment: "Love the focus on authentic community building 💪", likes: 98, timestamp: "6h ago" },
            { author: "Sarah Johnson", comment: "These trend predictions are always so accurate", likes: 87, timestamp: "7h ago" }
          ]
        },
        {
          id: "post_2_5",
          title: "Social Media Strategy Masterclass",
          colors: ['#EC4899'],
          scheduled_date: `${dayAfterTomorrow}T15:00:00`, // Future - scheduled
          temperature: null,
          stats: null,
          strategy: "Buyers Intent",
          comments: []
        }
      ]
    },
    {
      id: 3,
      title: "New strategy test",
      color: '#3B82F6', // Blue
      goal: {
        cadence: 'weekly',
        start_date: "2024-03-15T00:00:00",
        videos_count: 3
      },
      engagement_rate: 0.0,
      strategy: "Engage-Post-Engage",
      tasks: [
        { 
          id: 'task_1', 
          title: "Engage with #growthhacking", 
          goal: 5, 
          complete: 1,  
          query: {
            type: "hashtag",
            value: "#growthhacking"
          },
        },
      ],
      videos: [
        {
          id: "post_3_2",
          title: "Testing New Growth Strategy Results",
          colors: ['#3B82F6'],
          scheduled_date: null,
          temperature: null,
          stats: null,
          strategy: null,
          comments: [],
        },
        {
          id: "post_3_1",
          title: "Testing New Content Strategy Framework",
          colors: ['#3B82F6'],
          scheduled_date: `${yesterday}T18:00:00`,
          temperature: 0.5,
          stats: {
            comments: 3,
            shares: 78,
            likes: 423
          },
          strategy: null,
          comments: [
            { author: "Growth Hacker Pro", comment: "Love this new approach! The engagement metrics are promising.", likes: 45, timestamp: "3h ago" },
            { author: "Marketing Maven", comment: "Interesting strategy! How long did the pre-engagement phase take?", likes: 32, timestamp: "4h ago" },
            { author: "Digital Strategist", comment: "The results speak for themselves. Great work!", likes: 28, timestamp: "5h ago" }
          ]
        }
      ],
      engagement_rate_history: [],
    }
  ],
  inactive: [
    {
      id: 5,
      title: "Past Campaigns",
      color: '#9CA3AF', // Gray
      goal: {
        cadence: 'completed',
        start_date: "2024-01-01T00:00:00",
        videos_count: 4
      },
      engagement_rate: 5.7,
      strategy: "Peak Hours",
      engagement_rate_history: [
        { date: "2024-01-01", value: 2.1 },
        { date: "2024-01-15", value: 3.8 },
        { date: "2024-02-01", value: 4.9 },
        { date: "2024-02-15", value: 5.7 }
      ],
      videos: [
        {
          id: "post_5_1",
          title: "Q4 Marketing Review",
          colors: ['#9CA3AF'],
          temperature: 0.5, // Medium - moderately viral
          strategy: "Biggest fan"
        }
      ]
    },
    {
      id: 6,
      title: "Holiday Special",
      color: '#9CA3AF', // Gray
      goal: {
        cadence: 'completed',
        start_date: "2023-12-15T00:00:00",
        videos_count: 3
      },
      engagement_rate: 3.2,
      strategy: "Trending hashtag: #examplehash",
      engagement_rate_history: [
        { date: "2023-12-15", value: 1.2 },
        { date: "2023-12-20", value: 2.1 },
        { date: "2023-12-25", value: 2.8 },
        { date: "2023-12-31", value: 3.2 }
      ],
      videos: [
        {
          id: "post_6_1",
          title: "End of Year Highlights",
          colors: ['#9CA3AF'],
          temperature: 0.1, // Low - not viral
          strategy: "Biggest fan"
        }
      ]
    },
    {
      id: 7,
      title: "Beta Testing",
      color: '#9CA3AF', // Gray
      goal: {
        cadence: 'completed',
        start_date: "2023-11-01T00:00:00",
        videos_count: 2
      },
      engagement_rate: 1.8,
      strategy: "9 9 3",
      engagement_rate_history: [
        { date: "2023-11-01", value: 0.8 },
        { date: "2023-11-15", value: 1.4 },
        { date: "2023-11-30", value: 1.8 }
      ],
      videos: []
    }
  ]
};

const ViewToggle = ({ view, onViewChange }) => {
  return (
    <div className="inline-flex bg-gray-800/50 rounded-xl p-1 mb-6">
      <button
        onClick={() => onViewChange('grid')}
        className={cn(
          "inline-flex items-center px-3 py-1.5 rounded-xl text-sm font-medium transition-all duration-200",
          view === 'grid' 
            ? "bg-gradient-to-r from-purple-500 to-indigo-500 text-white shadow-lg shadow-purple-500/25" 
            : "text-gray-400 hover:text-gray-300 hover:bg-gray-700/50"
        )}
      >
        <LayoutGrid className="w-4 h-4 mr-2" />
        Grid View
      </button>
      <button
        onClick={() => onViewChange('flow')}
        className={cn(
          "inline-flex items-center px-3 py-1.5 rounded-xl text-sm font-medium transition-all duration-200",
          view === 'flow' 
            ? "bg-gradient-to-r from-purple-500 to-indigo-500 text-white shadow-lg shadow-purple-500/25" 
            : "text-gray-400 hover:text-gray-300 hover:bg-gray-700/50"
        )}
      >
        <GitBranch className="w-4 h-4 mr-2" />
        Flow View
      </button>
    </div>
  );
};

// Add new animation constants
const SPRING_CONFIG = {
  type: "spring",
  stiffness: 300,
  damping: 30
};

const ORBITAL_ANIMATION = {
  rotate: {
    animate: { rotate: 360 },
    transition: { 
      duration: 60,
      repeat: Infinity,
      ease: "linear"
    }
  }
};

const Particle = ({ color, delay = 0 }) => {
  const pathLength = useMotionValue(0);
  const opacity = useTransform(pathLength, [0, 0.5, 1], [0, 1, 0]);
  
  return (
    <motion.circle
      r="2"
      fill={color}
      style={{ opacity }}
      animate={{
        pathLength: [0, 1],
        pathOffset: [0, 1]
      }}
      transition={{
        duration: 2,
        delay,
        repeat: Infinity,
        ease: "linear"
      }}
    />
  );
};

const NodeDetails = ({ node, data, color, filters }) => {
  const variants = {
    hidden: { 
      opacity: 0, 
      x: -20,
      scale: 0.95
    },
    visible: { 
      opacity: 1, 
      x: 0,
      scale: 1,
      transition: { 
        type: "spring", 
        stiffness: 500, 
        damping: 30,
        staggerChildren: 0.1
      }
    },
    exit: {
      opacity: 0,
      x: 20,
      scale: 0.95,
      transition: {
        duration: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0 }
  };

  const getTimeRange = () => {
    switch (filters.timeRange) {
      case '7d': return 7;
      case '30d': return 30;
      case '90d': return 90;
      default: return 7;
    }
  };

  const getFilteredHistory = (history) => {
    if (!history?.length) return [];
    const days = getTimeRange();
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - days);
    return history.filter(h => new Date(h.date) >= cutoff);
  };

  const getDetails = () => {
    switch (node) {
      case 'strategy':
        const implementationTrend = data.strategy_implementation_history || [];
        const filteredTrend = getFilteredHistory(implementationTrend);
        const currentImpl = data.strategy_implementation || 0;
        const prevImpl = filteredTrend[filteredTrend.length - 2]?.value || 0;
        const implChange = ((currentImpl - prevImpl) / prevImpl * 100).toFixed(1);

        return (
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium text-gray-200 mb-3">Strategy Insights</h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-lg bg-purple-500/10">
                  <div className="text-xs text-purple-400">Implementation</div>
                  <div className="mt-1 text-lg font-semibold text-gray-200">
                    {Math.round(currentImpl * 100)}%
                    <span className={cn(
                      "ml-1 text-xs",
                      implChange > 0 ? "text-green-400" : "text-red-400"
                    )}>
                      {implChange > 0 ? '+' : ''}{implChange}%
                    </span>
                  </div>
                </div>
                <div className="p-3 rounded-lg bg-blue-500/10">
                  <div className="text-xs text-blue-400">Tasks Complete</div>
                  <div className="mt-1 text-lg font-semibold text-gray-200">
                    {data.tasks?.filter(t => t.complete === t.goal).length || 0}/{data.tasks?.length || 0}
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-xs font-medium text-gray-400 mb-2">Implementation Trend</h4>
              <div className="h-24">
                <svg className="w-full h-full" viewBox="0 0 100 40">
                  <defs>
                    <linearGradient id="strategyGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={color} stopOpacity="0.3" />
                      <stop offset="100%" stopColor={color} stopOpacity="0.05" />
                    </linearGradient>
                  </defs>
                  <path
                    d={filteredTrend.map((point, i, arr) => {
                      const x = (i / (arr.length - 1)) * 100;
                      const y = 40 - (point.value * 40);
                      return i === 0 ? `M ${x} ${y}` : `L ${x} ${y}`;
                    }).join(' ')}
                    stroke={color}
                    strokeWidth="2"
                    fill="none"
                  />
                  <path
                    d={`${filteredTrend.map((point, i, arr) => {
                      const x = (i / (arr.length - 1)) * 100;
                      const y = 40 - (point.value * 40);
                      return i === 0 ? `M ${x} ${y}` : `L ${x} ${y}`;
                    }).join(' ')} L 100 40 L 0 40 Z`}
                    fill="url(#strategyGradient)"
                  />
                </svg>
              </div>
            </div>

            <div className="space-y-2">
              <h4 className="text-xs font-medium text-gray-400">Recommendations</h4>
              <div className="flex items-start gap-2 text-xs">
                <ArrowUpRight className="w-3 h-3 text-green-400 mt-0.5" />
                <span className="text-gray-300">
                  {currentImpl >= 0.8 
                    ? "Strategy is performing well. Consider increasing scope."
                    : "Focus on task completion to improve implementation."}
                </span>
              </div>
            </div>
          </div>
        );

      case 'videos':
        const totalViews = data.videos.reduce((sum, v) => sum + (v.stats?.views || 0), 0);
        const avgEngagement = data.videos.reduce((sum, v) => sum + (v.stats?.engagement || 0), 0) / data.videos.length;
        
        return (
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium text-gray-200 mb-3">Content Performance</h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-lg bg-blue-500/10">
                  <div className="text-xs text-blue-400">Total Views</div>
                  <div className="mt-1 text-lg font-semibold text-gray-200">
                    {totalViews.toLocaleString()}
                  </div>
                </div>
                <div className="p-3 rounded-lg bg-green-500/10">
                  <div className="text-xs text-green-400">Avg. Engagement</div>
                  <div className="mt-1 text-lg font-semibold text-gray-200">
                    {avgEngagement.toFixed(1)}%
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-xs font-medium text-gray-400 mb-2">Top Performing</h4>
              <div className="space-y-2">
                {data.videos
                  .sort((a, b) => (b.stats?.engagement || 0) - (a.stats?.engagement || 0))
                  .slice(0, 3)
                  .map((video, index) => (
                    <div 
                      key={video.id}
                      className="flex items-center gap-3 p-2 rounded-lg bg-gray-800/50 hover:bg-gray-800 transition-colors cursor-pointer"
                    >
                      <div className="w-8 h-8 rounded-lg bg-gray-700 flex items-center justify-center">
                        <Film className="w-4 h-4 text-gray-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium text-gray-300 truncate">
                          {video.title}
                        </div>
                        <div className="flex items-center gap-2 text-[10px] text-gray-500">
                          <span>{video.stats?.views?.toLocaleString() || 0} views</span>
                          <span>•</span>
                          <span>{video.stats?.engagement || 0}% engagement</span>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>

            <div className="space-y-2">
              <h4 className="text-xs font-medium text-gray-400">Best Posting Times</h4>
              <div className="grid grid-cols-7 gap-1">
                {Array.from({ length: 7 }).map((_, i) => (
                  <div 
                    key={i}
                    className="text-center"
                  >
                    <div className="text-[10px] text-gray-500 mb-1">
                      {['S', 'M', 'T', 'W', 'T', 'F', 'S'][i]}
                    </div>
                    <div className="h-12 bg-gray-800 rounded-md relative">
                      <div 
                        className="absolute bottom-0 left-0 right-0 bg-blue-500 rounded-md transition-all"
                        style={{ 
                          height: `${Math.random() * 100}%`,
                          opacity: 0.2
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'engagement':
        const engagementData = data.engagement_rate_history || [];
        const currentRate = data.engagement_rate;
        const previousRate = engagementData[engagementData.length - 2]?.value || 0;
        const change = ((currentRate - previousRate) / previousRate * 100).toFixed(1);
        
        return (
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-gray-200">Engagement Analysis</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 rounded-lg bg-gray-800/50">
                <div className="text-xs text-gray-400 mb-1">Current Rate</div>
                <div className="text-lg font-semibold text-gray-200">{currentRate}%</div>
              </div>
              <div className="p-3 rounded-lg bg-gray-800/50">
                <div className="text-xs text-gray-400 mb-1">Change</div>
                <div className={cn(
                  "text-lg font-semibold",
                  change > 0 ? "text-green-400" : "text-red-400"
                )}>
                  {change > 0 ? '+' : ''}{change}%
                </div>
              </div>
            </div>
            <div className="h-20 w-full">
              {/* Mini sparkline chart here */}
              <svg className="w-full h-full">
                {engagementData.map((point, i, arr) => {
                  const x = (i / (arr.length - 1)) * 100;
                  const y = 100 - (point.value / Math.max(...arr.map(p => p.value)) * 100);
                  return i === 0 ? `M ${x} ${y}` : `L ${x} ${y}`;
                }).join(' ')}
              </svg>
            </div>
          </div>
        );
      case 'tasks':
        if (!data.tasks?.length) return null;
        return (
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-gray-200">Task Progress</h3>
            <div className="space-y-2">
              {data.tasks.map((task, index) => (
                <div 
                  key={index}
                  className="flex items-center gap-3 p-2 rounded-lg bg-gray-800/50"
                >
                  <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center">
                    <span className="text-xs text-green-400">{task.complete}/{task.goal}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-gray-300 truncate">{task.title}</div>
                    <div className="mt-1 h-1 bg-gray-700 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-green-500 rounded-full transition-all duration-300"
                        style={{ width: `${(task.complete / task.goal) * 100}%` }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      case 'metrics':
        const totalLikes = data.videos.reduce((sum, v) => sum + (v.stats?.likes || 0), 0);
        const totalComments = data.videos.reduce((sum, v) => sum + (v.stats?.comments || 0), 0);
        const totalShares = data.videos.reduce((sum, v) => sum + (v.stats?.shares || 0), 0);
        
        return (
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-gray-200">Performance Metrics</h3>
            <div className="grid grid-cols-3 gap-2">
              <div className="text-center p-2 rounded-lg bg-gray-800/50">
                <Heart className="w-4 h-4 text-red-400 mx-auto mb-1" />
                <div className="text-lg font-semibold text-gray-200">{totalLikes}</div>
                <div className="text-xs text-gray-400">Likes</div>
              </div>
              <div className="text-center p-2 rounded-lg bg-gray-800/50">
                <MessageCircle className="w-4 h-4 text-blue-400 mx-auto mb-1" />
                <div className="text-lg font-semibold text-gray-200">{totalComments}</div>
                <div className="text-xs text-gray-400">Comments</div>
              </div>
              <div className="text-center p-2 rounded-lg bg-gray-800/50">
                <Share2 className="w-4 h-4 text-green-400 mx-auto mb-1" />
                <div className="text-lg font-semibold text-gray-200">{totalShares}</div>
                <div className="text-xs text-gray-400">Shares</div>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        variants={variants}
        initial="hidden"
        animate="visible"
        exit="exit"
        className={cn(
          "absolute p-4 rounded-xl border border-gray-800/50",
          "bg-gray-900/95 backdrop-blur-md shadow-xl",
          "w-[300px] z-20"
        )}
        style={{
          left: 'calc(100% + 20px)',
          top: '50%',
          transform: 'translateY(-50%)'
        }}
      >
        <motion.div variants={itemVariants}>
          {getDetails()}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

const FlowConnector = ({ start, end, color, progress = 100, status = 'normal' }) => {
  const pathRef = useRef(null);
  const [pathLength, setPathLength] = useState(0);
  
  useEffect(() => {
    if (pathRef.current) {
      setPathLength(pathRef.current.getTotalLength());
    }
  }, []);

  const path = `M ${start.x} ${start.y} C ${start.x + 100} ${start.y}, ${end.x - 100} ${end.y}, ${end.x} ${end.y}`;

  const getStatusColor = () => {
    switch (status) {
      case 'ahead': return 'var(--color-green-500)';
      case 'behind': return 'var(--color-red-500)';
      default: return color;
    }
  };

  return (
    <svg
      className="absolute inset-0 pointer-events-none z-10"
      style={{ width: '100%', height: '100%' }}
    >
      <defs>
        <linearGradient id={`gradient-${start.x}-${start.y}`} gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor={getStatusColor()} stopOpacity="0.2" />
          <stop offset="100%" stopColor={getStatusColor()} stopOpacity="0.6" />
        </linearGradient>
        <filter id="glow">
          <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
          <feMerge>
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
      </defs>
      
      {/* Background path */}
      <path
        d={path}
        stroke="rgba(255,255,255,0.1)"
        strokeWidth="2"
        fill="none"
      />
      
      {/* Progress path */}
      <path
        ref={pathRef}
        d={path}
        stroke={`url(#gradient-${start.x}-${start.y})`}
        strokeWidth="2"
        fill="none"
        className="transition-all duration-300"
        style={{
          strokeDasharray: pathLength,
          strokeDashoffset: pathLength - (pathLength * (progress / 100)),
          filter: status === 'ahead' ? 'url(#glow)' : 'none'
        }}
      />
      
      {/* Animated particles */}
      {[0, 0.25, 0.5, 0.75].map((offset, i) => (
        <motion.circle
          key={i}
          r="2"
          fill={getStatusColor()}
          style={{ opacity: 0.6 }}
          initial={{ offset: 0 }}
          animate={{ 
            offset: [offset, offset + 1],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "linear",
            delay: i * 0.5
          }}
        >
          <animateMotion
            dur="2s"
            repeatCount="indefinite"
            path={path}
          />
        </motion.circle>
      ))}
      
      {/* Progress indicator */}
      {progress < 100 && (
        <text
          x={(start.x + end.x) / 2}
          y={(start.y + end.y) / 2}
          dy="-10"
          textAnchor="middle"
          className="text-xs fill-current text-gray-400"
        >
          {progress}%
        </text>
      )}
    </svg>
  );
};

const FlowNode = ({ data, type, color, onClick, isActive, oplae, filters }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [showQuickActions, setShowQuickActions] = useState(false);
  const controls = useAnimation();
  const [taskProgress, setTaskProgress] = useState({});

  // Add getFilteredHistory function
  const getFilteredHistory = (history) => {
    if (!history?.length) return [];
    
    const days = (() => {
      switch (filters.timeRange) {
        case '7d': return 7;
        case '30d': return 30;
        case '90d': return 90;
        default: return 7;
      }
    })();

    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - days);
    
    return history.filter(h => new Date(h.date) >= cutoff);
  };

  useEffect(() => {
    if (isActive) {
      controls.start({
        scale: 1.05,
        transition: { type: "spring", stiffness: 400, damping: 25 }
      });
    } else {
      controls.start({
        scale: 1,
        transition: { type: "spring", stiffness: 400, damping: 25 }
      });
    }
  }, [isActive]);

  const handleTaskProgress = (taskId, change) => {
    setTaskProgress(prev => {
      const task = oplae.tasks.find(t => t.id === taskId);
      if (!task) return prev;
      
      const currentProgress = prev[taskId] || task.complete;
      const newProgress = Math.max(0, Math.min(task.goal, currentProgress + change));
      
      return {
        ...prev,
        [taskId]: newProgress
      };
    });
  };

  const getTaskProgress = (taskId) => {
    const task = oplae.tasks.find(t => t.id === taskId);
    return taskProgress[taskId] ?? task.complete;
  };

  const pulseVariants = {
    inactive: {
      boxShadow: `0 0 0 0px ${color}20`,
    },
    active: {
      boxShadow: [
        `0 0 0 0px ${color}20`,
        `0 0 0 4px ${color}10`,
        `0 0 0 8px ${color}05`,
        `0 0 0 0px ${color}00`,
      ],
      transition: {
        duration: 2,
        repeat: Infinity,
        ease: "easeInOut",
      },
    },
  };

  const containerProps = {
    onMouseEnter: () => {
      setIsHovered(true);
      setShowQuickActions(true);
    },
    onMouseLeave: () => {
      setIsHovered(false);
      setShowQuickActions(false);
    },
    onClick,
    animate: controls,
    variants: pulseVariants,
    initial: "inactive",
    whileHover: { scale: 1.02 },
    className: cn(
      "relative rounded-lg transition-all duration-200",
      "backdrop-blur-lg",
      isActive && "z-10"
    ),
    style: {
      transformOrigin: "center center",
    },
  };

  const renderPlatformBadges = () => {
    if (!filters.showPlatforms) return null;
    
    const platforms = {
      instagram: { color: '#E4405F', icon: Instagram },
      tiktok: { color: '#000000', icon: TiktokIcon },
      youtube: { color: '#FF0000', icon: Play }
    };

    return (
      <div className="absolute -top-2 -right-2 flex -space-x-1">
        {Object.entries(platforms).map(([platform, { color, icon: Icon }]) => (
          <div
            key={platform}
            className="w-4 h-4 rounded-full flex items-center justify-center ring-2 ring-gray-900"
            style={{ backgroundColor: color }}
          >
            <Icon className="w-2 h-2 text-white" />
          </div>
        ))}
      </div>
    );
  };

  const renderPerformanceIndicator = () => {
    if (!filters.showPerformance) return null;

    const getPerformanceColor = () => {
      const performance = type === 'engagement' ? oplae.engagement_rate : 0.75;
      if (performance >= 0.8) return 'bg-green-500';
      if (performance >= 0.5) return 'bg-yellow-500';
      return 'bg-red-500';
    };

    return (
      <div className="absolute -top-2 -left-2 w-4 h-4 rounded-full flex items-center justify-center ring-2 ring-gray-900">
        <div className={cn("w-2 h-2 rounded-full", getPerformanceColor())} />
      </div>
    );
  };

  const renderQuickActions = () => {
    if (!showQuickActions) return null;

    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 10 }}
        className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 flex items-center gap-2 bg-gray-800/90 backdrop-blur-sm rounded-full px-3 py-1.5 shadow-lg border border-gray-700/50"
      >
        {type === 'videos' && (
          <>
            <button 
              className="p-1.5 rounded-full hover:bg-gray-700/80 text-gray-400 hover:text-gray-200 transition-all duration-200 hover:scale-110"
              title="Play video"
            >
              <Play className="w-3.5 h-3.5" />
            </button>
            <button 
              className="p-1.5 rounded-full hover:bg-gray-700/80 text-gray-400 hover:text-gray-200 transition-all duration-200 hover:scale-110"
              title="Add video"
            >
              <Plus className="w-3.5 h-3.5" />
            </button>
          </>
        )}
        {type === 'strategy' && (
          <button 
            className="p-1.5 rounded-full hover:bg-gray-700/80 text-gray-400 hover:text-gray-200 transition-all duration-200 hover:scale-110"
            title="Strategy settings"
          >
            <Settings className="w-3.5 h-3.5" />
          </button>
        )}
        {type === 'engagement' && (
          <button 
            className="p-1.5 rounded-full hover:bg-gray-700/80 text-gray-400 hover:text-gray-200 transition-all duration-200 hover:scale-110"
            title="View analytics"
          >
            <BarChart2 className="w-3.5 h-3.5" />
          </button>
        )}
      </motion.div>
    );
  };

  const getNodeContent = () => {
    switch (type) {
      case 'strategy':
        const implementationTrend = oplae.strategy_implementation_history || [];
        const filteredTrend = getFilteredHistory(implementationTrend);
        const currentImpl = oplae.strategy_implementation || 0;
        const prevImpl = filteredTrend[filteredTrend.length - 2]?.value || 0;
        const implChange = ((currentImpl - prevImpl) / prevImpl * 100).toFixed(1);

        return (
          <motion.div {...containerProps}>
            <div className={cn(
              "p-4 rounded-lg border transition-all duration-200",
              "bg-gradient-to-br from-gray-900/90 to-gray-800/90",
              isActive ? "border-purple-500" : "border-gray-800/50",
              "hover:border-purple-400 cursor-pointer",
              isHovered && "shadow-lg shadow-purple-500/10"
            )}>
              {renderPlatformBadges()}
              {renderPerformanceIndicator()}
              <div className="flex items-start gap-3">
                <div className={cn(
                  "p-2 rounded-lg transition-colors duration-200",
                  isActive ? "bg-purple-500/30" : "bg-purple-500/20"
                )}>
                  <Zap className={cn(
                    "w-5 h-5 transition-colors duration-200",
                    isActive ? "text-purple-300" : "text-purple-400"
                  )} />
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-200">Strategy</h3>
                  <p className="text-xs text-gray-400 mt-1">{oplae.strategy}</p>
                  <div className="mt-2 relative pt-1">
                    <div className="flex mb-2 items-center justify-between">
                      <div>
                        <span className="text-xs font-semibold inline-block text-purple-400">
                          Implementation
                        </span>
                      </div>
                      <div className="text-right">
                        <span className="text-xs font-semibold inline-block text-purple-400">
                          {Math.round(currentImpl * 100)}%
                          <span className={cn(
                            "ml-1 text-xs",
                            implChange > 0 ? "text-green-400" : "text-red-400"
                          )}>
                            {implChange > 0 ? '+' : ''}{implChange}%
                          </span>
                        </span>
                      </div>
                    </div>
                    <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-purple-900/50">
                      <div 
                        className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-purple-500 transition-all duration-300"
                        style={{ width: `${currentImpl * 100}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>
              {renderQuickActions()}
            </div>
          </motion.div>
        );

      case 'videos':
        return (
          <div className="space-y-2">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-200">Content</h3>
              <span className="text-xs text-gray-400">
                {oplae.videos.length}/{oplae.goal?.videos_count || 0} Videos
              </span>
            </div>
            <div className="space-y-2 max-h-[200px] overflow-y-auto pr-2">
              {oplae.videos.map((video, index) => (
                <motion.div
                  key={index}
                  className={cn(
                    "p-3 rounded-lg border transition-all duration-200",
                    "bg-gradient-to-br from-gray-900/90 to-gray-800/90",
                    isActive ? "border-blue-500" : "border-gray-800/50",
                    "hover:border-blue-400 cursor-pointer group"
                  )}
                  whileHover={{ scale: 1.02 }}
                >
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <div className="w-12 h-12 rounded-lg bg-gray-800 flex items-center justify-center">
                        <Film className="w-5 h-5 text-blue-400" />
                      </div>
                      {video.temperature > 0.7 && (
                        <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-orange-500 ring-2 ring-gray-900" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium text-gray-300 truncate">{video.title}</div>
                      <div className="flex items-center gap-3 mt-1 text-[10px] text-gray-500">
                        <div className="flex items-center gap-1">
                          <Heart className="w-3 h-3" />
                          {video.stats?.likes?.toLocaleString() || 0}
                        </div>
                        <div className="flex items-center gap-1">
                          <MessageCircle className="w-3 h-3" />
                          {video.stats?.comments?.toLocaleString() || 0}
                        </div>
                        <div className="flex items-center gap-1">
                          <Share2 className="w-3 h-3" />
                          {video.stats?.shares?.toLocaleString() || 0}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Strategy Badge */}
                  {video.strategy && (
                    <div className="mt-2 flex items-center justify-between">
                      <div className="px-2 py-1 rounded-full bg-green-500/10 text-green-400 text-xs">
                        {video.strategy}
                      </div>
                      <button className="opacity-0 group-hover:opacity-100 transition-opacity">
                        <ArrowUpRight className="w-4 h-4 text-gray-400 hover:text-gray-300" />
                      </button>
                    </div>
                  )}

                  {/* Comments Section - Only show when video is active */}
                  {isActive && video.comments?.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-800">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-xs font-medium text-gray-400">
                          Top Comments to Reply
                        </div>
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <div className="flex items-center gap-1">
                            <MessageCircle className="w-3 h-3" />
                            {video.stats?.comments?.toLocaleString() || 0}
                          </div>
                          <div className="flex items-center gap-1">
                            <Share2 className="w-3 h-3" />
                            {video.stats?.shares?.toLocaleString() || 0}
                          </div>
                          <div className="flex items-center gap-1">
                            <Heart className="w-3 h-3" />
                            {video.stats?.likes?.toLocaleString() || 0}
                          </div>
                        </div>
                      </div>

                      {/* Strategy Badge */}
                      {video.strategy && (
                        <div className="mb-3">
                          <div className="px-2 py-1 rounded-full bg-green-500/10 text-green-400 text-xs inline-flex items-center gap-1">
                            <Target className="w-3 h-3" />
                            {video.strategy}
                          </div>
                        </div>
                      )}

                      <div className="space-y-3">
                        {video.comments.slice(0, 3).map((comment, idx) => (
                          <div key={idx} className="relative group">
                            <div className="p-3 rounded-lg bg-gray-800/50 hover:bg-gray-800 transition-colors">
                              <div className="flex items-start gap-2">
                                <div className="w-6 h-6 rounded-full bg-gray-700 flex items-center justify-center text-[10px] text-gray-300">
                                  {comment.author.charAt(0)}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between">
                                    <span className="font-medium text-xs text-gray-300">{comment.author}</span>
                                    <span className="text-[10px] text-gray-500">{comment.timestamp}</span>
                                  </div>
                                  <p className="text-xs text-gray-400 mt-0.5">{comment.comment}</p>
                                  
                                  {/* Reply Input - Show on hover */}
                                  <div className="mt-2 flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <input
                                      type="text"
                                      placeholder="Write a reply..."
                                      className="flex-1 bg-gray-900/50 rounded-full px-3 py-1 text-xs text-gray-300 placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                    />
                                    <button className="px-2 py-1 rounded-full bg-blue-500 hover:bg-blue-600 text-[10px] text-white font-medium transition-colors">
                                      Reply
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Engagement Stats */}
                            <div className="absolute right-2 top-2 flex items-center gap-2 text-[10px] text-gray-500">
                              <div className="flex items-center gap-1">
                                <Heart className="w-2.5 h-2.5" />
                                {comment.likes}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      {video.comments.length > 3 && (
                        <button className="mt-3 text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1">
                          <Plus className="w-3 h-3" />
                          View {video.comments.length - 3} more comments
                        </button>
                      )}
                    </div>
                  )}

                  {/* Comment Input - Show on hover */}
                  <div className="mt-3 pt-3 border-t border-gray-800 opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-gray-800 flex items-center justify-center">
                        <User className="w-3 h-3 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        placeholder="Write a comment..."
                        className="flex-1 bg-gray-800/50 rounded-full px-3 py-1 text-xs text-gray-300 placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                      <button className="p-1 rounded-full hover:bg-gray-800">
                        <Send className="w-3 h-3 text-gray-400 hover:text-gray-300" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
            {renderQuickActions()}
          </div>
        );

      case 'engagement':
        const engagementData = oplae.engagement_rate_history || [];
        const currentRate = oplae.engagement_rate;
        const previousRate = engagementData[engagementData.length - 2]?.value || 0;
        const change = ((currentRate - previousRate) / previousRate * 100).toFixed(1);

        return (
          <motion.div
            className={cn(
              "p-4 rounded-lg border transition-all duration-200",
              "bg-gradient-to-br from-gray-900/90 to-gray-800/90",
              isActive ? "border-green-500" : "border-gray-800/50",
              "hover:border-green-400 cursor-pointer"
            )}
            whileHover={{ scale: 1.02 }}
          >
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-sm font-medium text-gray-200">Engagement</h3>
                <div className="mt-2 text-2xl font-bold text-gray-100">
                  {currentRate}%
                  <span className={cn(
                    "ml-2 text-sm font-medium",
                    change > 0 ? "text-green-400" : "text-red-400"
                  )}>
                    {change > 0 ? '+' : ''}{change}%
                  </span>
                </div>
              </div>
              <div className="p-2 rounded-lg bg-green-500/20">
                <TrendingUp className="w-5 h-5 text-green-400" />
              </div>
            </div>
            <div className="h-16 mt-4">
              <svg className="w-full h-full" viewBox="0 0 100 40">
                <defs>
                  <linearGradient id="gradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={color} stopOpacity="0.3" />
                    <stop offset="100%" stopColor={color} stopOpacity="0.05" />
                  </linearGradient>
                </defs>
                <path
                  d={engagementData.map((point, i, arr) => {
                    const x = (i / (arr.length - 1)) * 100;
                    const y = 40 - (point.value / Math.max(...arr.map(p => p.value)) * 40);
                    return i === 0 ? `M ${x} ${y}` : `L ${x} ${y}`;
                  }).join(' ')}
                  stroke={color}
                  strokeWidth="2"
                  fill="none"
                />
                <path
                  d={`${engagementData.map((point, i, arr) => {
                    const x = (i / (arr.length - 1)) * 100;
                    const y = 40 - (point.value / Math.max(...arr.map(p => p.value)) * 40);
                    return i === 0 ? `M ${x} ${y}` : `L ${x} ${y}`;
                  }).join(' ')} L 100 40 L 0 40 Z`}
                  fill="url(#gradient)"
                />
              </svg>
            </div>
          </motion.div>
        );

      case 'tasks':
        if (!oplae.tasks?.length) return null;
        return (
          <motion.div {...containerProps}>
            <div className={cn(
              "p-4 rounded-lg border transition-all duration-200",
              "bg-gradient-to-br from-gray-900/90 to-gray-800/90",
              isActive ? "border-indigo-500" : "border-gray-800/50",
              "hover:border-indigo-400 cursor-pointer"
            )}>
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-sm font-medium text-gray-200">Tasks</h3>
                  <p className="text-xs text-gray-400 mt-1">
                    {oplae.tasks.filter(t => getTaskProgress(t.id) === t.goal).length}/{oplae.tasks.length} Complete
                  </p>
                </div>
                <div className="p-2 rounded-lg bg-indigo-500/20">
                  <CheckCircle2 className="w-5 h-5 text-indigo-400" />
                </div>
              </div>
              <div className="mt-4 space-y-3">
                {oplae.tasks.map((task, index) => (
                  <div key={task.id} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-indigo-500/20 flex items-center justify-center">
                      <span className="text-xs text-indigo-400">{getTaskProgress(task.id)}/{task.goal}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-gray-300 truncate">{task.title}</div>
                      <div className="flex items-center gap-2 mt-1">
                        <button 
                          className="p-0.5 rounded hover:bg-indigo-500/20 transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleTaskProgress(task.id, -1);
                          }}
                        >
                          <Minus className="w-3 h-3 text-indigo-400" />
                        </button>
                        <div className="flex-1 h-1 bg-gray-800 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-indigo-500 rounded-full transition-all duration-300"
                            style={{ width: `${(getTaskProgress(task.id) / task.goal) * 100}%` }}
                          />
                        </div>
                        <button 
                          className="p-0.5 rounded hover:bg-indigo-500/20 transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleTaskProgress(task.id, 1);
                          }}
                        >
                          <Plus className="w-3 h-3 text-indigo-400" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {renderQuickActions()}
            </div>
          </motion.div>
        );

      case 'metrics':
        const { total_likes, total_comments, total_shares } = oplae.metrics || {};
        
        return (
          <motion.div
            className={cn(
              "p-4 rounded-lg border transition-all duration-200",
              "bg-gradient-to-br from-gray-900/90 to-gray-800/90",
              isActive ? "border-orange-500" : "border-gray-800/50",
              "hover:border-orange-400 cursor-pointer"
            )}
            whileHover={{ scale: 1.02 }}
          >
            <div className="flex items-start justify-between">
              <h3 className="text-sm font-medium text-gray-200">Performance</h3>
              <div className="p-2 rounded-lg bg-orange-500/20">
                <BarChart2 className="w-5 h-5 text-orange-400" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4 mt-4">
              <div>
                <div className="text-xl font-bold text-gray-100">{total_likes?.toLocaleString() || 0}</div>
                <div className="text-xs text-gray-400 mt-1">Likes</div>
              </div>
              <div>
                <div className="text-xl font-bold text-gray-100">{total_comments?.toLocaleString() || 0}</div>
                <div className="text-xs text-gray-400 mt-1">Comments</div>
              </div>
              <div>
                <div className="text-xl font-bold text-gray-100">{total_shares?.toLocaleString() || 0}</div>
                <div className="text-xs text-gray-400 mt-1">Shares</div>
              </div>
            </div>
          </motion.div>
        );

      default:
        return null;
    }
  };

  return (
    <motion.div
      initial={false}
      animate={isActive ? "active" : "inactive"}
      className="relative"
    >
      {getNodeContent()}
      <AnimatePresence>
        {isActive && <NodeDetails node={type} data={oplae} color={color} filters={filters} />}
      </AnimatePresence>
    </motion.div>
  );
};

const FlowView = ({ oplaes }) => {
  const [selectedOplae, setSelectedOplae] = useState(oplaes.current[0]?.id);
  const [activeNode, setActiveNode] = useState(null);
  const [filters, setFilters] = useState({
    showPlatforms: false,
    showPerformance: false,
    timeRange: '7d'
  });
  
  const currentOplae = oplaes.current.find(o => o.id === selectedOplae);

  return (
    <div className="flex gap-8">
      {/* Left sidebar with Oplae list */}
      <div className="w-[320px] flex-shrink-0 space-y-3">
        <h2 className="text-lg font-semibold text-gray-200 mb-4">Active Oplaes</h2>
        <div className="space-y-3">
          {oplaes.current.map(oplae => (
            <OplaeListItem
              key={oplae.id}
              oplae={oplae}
              isActive={oplae.id === selectedOplae}
              onClick={() => {
                setSelectedOplae(oplae.id);
                setActiveNode(null);
              }}
            />
          ))}
        </div>

        {/* Flow Insights Panel - Moved inside the left sidebar */}
        <div className="mt-8 bg-gray-800/50 backdrop-blur-sm p-4 rounded-lg border border-gray-700">
          <h3 className="text-sm font-semibold text-gray-300 mb-3">Flow Insights</h3>
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="text-gray-400">Content Velocity</span>
                <span className="text-green-400">+22% WoW</span>
              </div>
              <div className="h-1 bg-gray-700 rounded-full overflow-hidden">
                <div className="h-full w-3/4 bg-green-500 rounded-full" />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="text-gray-400">Engagement Trend</span>
                <span className="text-purple-400">↑ 1.4x Avg</span>
              </div>
              <div className="h-1 bg-gray-700 rounded-full overflow-hidden">
                <div className="h-full w-1/2 bg-purple-500 rounded-full" />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="text-gray-400">Task Completion</span>
                <span className="text-blue-400">83%</span>
              </div>
              <div className="h-1 bg-gray-700 rounded-full overflow-hidden">
                <div className="h-full w-4/5 bg-blue-500 rounded-full" />
              </div>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-gray-700">
            <h4 className="text-xs font-medium text-gray-400 mb-2">Recommendations</h4>
            <div className="space-y-2">
              <div className="flex items-start gap-2 text-xs">
                <ArrowUpRight className="w-3 h-3 text-green-400 mt-0.5" />
                <span className="text-gray-300">Increase video frequency by 20% to match peak engagement times</span>
              </div>
              <div className="flex items-start gap-2 text-xs">
                <ArrowUpRight className="w-3 h-3 text-green-400 mt-0.5" />
                <span className="text-gray-300">Focus on Instagram Reels for higher reach potential</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right side with flow visualization */}
      <div className="flex-1 relative min-h-[600px] border-l border-gray-800 pl-8">
        {/* Filter controls */}
        <div className="absolute top-4 right-4 flex gap-2 z-10">
          <button 
            onClick={() => setFilters(f => ({ ...f, showPlatforms: !f.showPlatforms }))}
            className={cn(
              "px-3 py-1.5 rounded-xl text-sm font-medium transition-all duration-200",
              filters.showPlatforms 
                ? "bg-gradient-to-r from-purple-500 to-indigo-500 text-white shadow-lg shadow-purple-500/25" 
                : "bg-gray-800/50 text-gray-300 hover:bg-gray-700/50 hover:text-gray-200"
            )}
          >
            Show Platforms
          </button>
          <button 
            onClick={() => setFilters(f => ({ ...f, showPerformance: !f.showPerformance }))}
            className={cn(
              "px-3 py-1.5 rounded-xl text-sm font-medium transition-all duration-200",
              filters.showPerformance 
                ? "bg-gradient-to-r from-purple-500 to-indigo-500 text-white shadow-lg shadow-purple-500/25" 
                : "bg-gray-800/50 text-gray-300 hover:bg-gray-700/50 hover:text-gray-200"
            )}
          >
            Performance Mode
          </button>
          <select
            value={filters.timeRange}
            onChange={(e) => setFilters(f => ({ ...f, timeRange: e.target.value }))}
            className="px-3 py-1.5 rounded-xl bg-gray-800/50 text-gray-300 text-sm font-medium hover:bg-gray-700/50 hover:text-gray-200 transition-all duration-200 border border-gray-700/50"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
          </select>
        </div>

        {/* Background Pattern */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <svg width="100%" height="100%" className="opacity-[0.02]">
            <defs>
              <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" strokeWidth="1"/>
              </pattern>
              <pattern id="circles" width="80" height="80" patternUnits="userSpaceOnUse">
                <circle cx="40" cy="40" r="30" fill="none" stroke="currentColor" strokeWidth="1"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)"/>
            <rect width="100%" height="100%" fill="url(#circles)"/>
          </svg>
        </div>

        {currentOplae && (
          <motion.div
            key={currentOplae.id}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            className="relative"
          >
            {/* Title and description */}
            <div className="mb-8">
              <motion.h2 
                className="text-2xl font-bold text-gray-100"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                {currentOplae.title}
              </motion.h2>
              <motion.p 
                className="text-gray-400 mt-2"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                {currentOplae.strategy} • {currentOplae.engagement_rate}% Engagement
              </motion.p>
            </div>

            {/* Flow visualization */}
            <motion.div 
              className="relative"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              <OplaeFlow 
                oplae={currentOplae}
                activeNode={activeNode}
                onNodeClick={setActiveNode}
                filters={filters}
              />
            </motion.div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

const OplaeListItem = ({ oplae, isActive, onClick }) => {
  return (
    <motion.div
      className={cn(
        "p-4 rounded-xl border transition-all duration-200 cursor-pointer",
        "bg-gradient-to-br from-gray-900/90 to-gray-800/90 backdrop-blur-md",
        isActive ? "border-gray-700 ring-2 ring-purple-500/20" : "border-gray-800/50",
        "hover:border-gray-700"
      )}
      onClick={onClick}
      whileHover={{ scale: 1.02 }}
      layout
    >
      <div className="flex items-start gap-3">
        <div 
          className="w-2.5 h-2.5 rounded-full mt-1.5 ring-1 ring-gray-800"
          style={{ backgroundColor: oplae.color }}
        />
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-medium text-gray-200">{oplae.title}</h3>
          <div className="mt-1 flex items-center gap-3 text-xs text-gray-400">
            <div className="flex items-center gap-1">
              <Film className="w-3.5 h-3.5" />
              <span>{oplae.videos.length}/{oplae.goal?.videos_count || 0}</span>
            </div>
            <div className="flex items-center gap-1">
              <TrendingUp className="w-3.5 h-3.5" />
              <span>{oplae.engagement_rate}%</span>
            </div>
          </div>
          {oplae.strategy && (
            <div className="mt-2 inline-block px-1.5 py-0.5 rounded text-xs bg-purple-500/20 text-purple-400">
              {oplae.strategy}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

const OplaeFlow = ({ oplae, activeNode, onNodeClick, filters }) => {
  const nodeRefs = {
    strategy: useRef(null),
    videos: useRef(null),
    engagement: useRef(null),
    tasks: useRef(null),
    metrics: useRef(null)
  };

  const [nodePositions, setNodePositions] = useState({});

  useEffect(() => {
    // Calculate node positions after mount and on window resize
    const updatePositions = () => {
      const positions = {};
      Object.entries(nodeRefs).forEach(([key, ref]) => {
        if (ref.current) {
          const rect = ref.current.getBoundingClientRect();
          positions[key] = {
            x: rect.x + rect.width / 2,
            y: rect.y + rect.height / 2
          };
        }
      });
      setNodePositions(positions);
    };

    updatePositions();
    window.addEventListener('resize', updatePositions);
    return () => window.removeEventListener('resize', updatePositions);
  }, [oplae.id]);

  return (
    <div className="relative">
      {/* Main Flow Layout */}
      <div className="grid grid-cols-3 gap-8">
        {/* Left Column: Strategy & Tasks */}
        <div className="space-y-8">
          <div ref={nodeRefs.strategy}>
            <FlowNode
              type="strategy"
              data={oplae}
              color={oplae.color}
              isActive={activeNode === 'strategy'}
              onClick={() => onNodeClick(activeNode === 'strategy' ? null : 'strategy')}
              oplae={oplae}
              filters={filters}
            />
          </div>
          {oplae.tasks?.length > 0 && (
            <div ref={nodeRefs.tasks}>
              <FlowNode
                type="tasks"
                data={oplae}
                color={oplae.color}
                isActive={activeNode === 'tasks'}
                onClick={() => onNodeClick(activeNode === 'tasks' ? null : 'tasks')}
                oplae={oplae}
                filters={filters}
              />
            </div>
          )}
        </div>

        {/* Center Column: Videos */}
        <div ref={nodeRefs.videos}>
          <FlowNode
            type="videos"
            data={oplae}
            color={oplae.color}
            isActive={activeNode === 'videos'}
            onClick={() => onNodeClick(activeNode === 'videos' ? null : 'videos')}
            oplae={oplae}
            filters={filters}
          />
        </div>

        {/* Right Column: Engagement & Metrics */}
        <div className="space-y-8">
          <div ref={nodeRefs.engagement}>
            <FlowNode
              type="engagement"
              data={oplae}
              color={oplae.color}
              isActive={activeNode === 'engagement'}
              onClick={() => onNodeClick(activeNode === 'engagement' ? null : 'engagement')}
              oplae={oplae}
              filters={filters}
            />
          </div>
          <div ref={nodeRefs.metrics}>
            <FlowNode
              type="metrics"
              data={oplae}
              color={oplae.color}
              isActive={activeNode === 'metrics'}
              onClick={() => onNodeClick(activeNode === 'metrics' ? null : 'metrics')}
              oplae={oplae}
              filters={filters}
            />
          </div>
        </div>
      </div>

      {/* Connectors */}
      {Object.keys(nodePositions).length > 0 && (
        <>
          {/* Strategy to Videos */}
          {nodePositions.strategy && nodePositions.videos && (
            <FlowConnector
              start={nodePositions.strategy}
              end={nodePositions.videos}
              color={oplae.color}
            />
          )}
          
          {/* Tasks to Videos */}
          {nodePositions.tasks && nodePositions.videos && (
            <FlowConnector
              start={nodePositions.tasks}
              end={nodePositions.videos}
              color={oplae.color}
            />
          )}
          
          {/* Videos to Engagement */}
          {nodePositions.videos && nodePositions.engagement && (
            <FlowConnector
              start={nodePositions.videos}
              end={nodePositions.engagement}
              color={oplae.color}
            />
          )}
          
          {/* Videos to Metrics */}
          {nodePositions.videos && nodePositions.metrics && (
            <FlowConnector
              start={nodePositions.videos}
              end={nodePositions.metrics}
              color={oplae.color}
            />
          )}
        </>
      )}
    </div>
  );
};

const Oplaes = () => {
  const { 
    oplaes = { active: [], inactive: [] },
    loading, 
    error, 
    fetchOplaes,
    activateOplae,
    deactivateOplae,
    destroyOplae
  } = useOplaes();  
  const [view, setView] = useState('grid');
  // Using example data for now
  const data = exampleOplaes;

  useEffect(() => {
    fetchOplaes();
  }, [fetchOplaes]);

  // Enhance the data processing to include all necessary fields
  const processOplaes = (oplaes) => {
    return oplaes.map(oplae => ({
      ...oplae,
      // Add computed fields that might be missing
      strategy_implementation: oplae.tasks ? 
        oplae.tasks.reduce((acc, task) => acc + (task.complete / task.goal), 0) / oplae.tasks.length : 
        0,
      strategy_implementation_history: oplae.engagement_rate_history?.map(entry => ({
        date: entry.date,
        value: Math.random() * 0.8 + 0.2 // This is temporary, should come from actual data
      })),
      metrics: {
        total_views: oplae.videos.reduce((sum, v) => sum + (v.stats?.views || 0), 0),
        total_likes: oplae.videos.reduce((sum, v) => sum + (v.stats?.likes || 0), 0),
        total_comments: oplae.videos.reduce((sum, v) => sum + (v.stats?.comments || 0), 0),
        total_shares: oplae.videos.reduce((sum, v) => sum + (v.stats?.shares || 0), 0),
        avg_engagement: oplae.videos.length ? 
          oplae.videos.reduce((sum, v) => sum + (v.stats?.engagement || 0), 0) / oplae.videos.length : 
          0
      }
    }));
  };

  // Separate active oplaes into current and upcoming based on goal_start_date
  const { current, upcoming } = (oplaes?.active || []).reduce((acc, oplae) => {
    if (oplae?.goal && oplae.goal.start_date) {
      const startDate = new Date(oplae.goal.start_date);
      if (startDate > now) {
        acc.upcoming.push(oplae);
      } else {
        acc.current.push(oplae);
      }
    } else {
      // If no goal or start_date, treat as current
      acc.current.push(oplae);
    }
    return acc;
  }, { current: [], upcoming: [] });

  // Process the data to include all necessary fields
  const processedCurrent = processOplaes(current);
  const processedUpcoming = processOplaes(upcoming);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-var(--navbar-height)-8rem)] p-4">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-12 sm:w-16 h-12 sm:h-16 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 text-white mb-4">
            <Loader2 className="h-6 sm:h-8 w-6 sm:w-8 animate-spin" />
          </div>
          <p className="text-gray-400">Loading your oplaes...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-var(--navbar-height)-8rem)] p-4">
        <div className="bg-[#151A21] rounded-2xl p-8 max-w-md w-full border border-gray-800">
          <div className="flex flex-col items-center justify-center text-center">
            <div className="bg-red-500/10 rounded-full p-6 mb-6">
              <Film className="h-8 w-8 text-red-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-100 mb-2">
              Error Loading Oplaes
            </h3>
            <p className="text-gray-400 mb-8">
              {error}
            </p>
            <Button
              onClick={() => fetchOplaes()}
              size="lg"
              className="bg-purple-500 hover:bg-purple-600 text-white rounded-full px-8"
            >
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cn(
      "min-h-screen bg-[#0B0F15]",
      "pt-[var(--navbar-height,_76px)]",
      "pb-[env(safe-area-inset-bottom,_0px)]",
      "overflow-x-hidden"
    )}>
      <div className="space-y-8 pb-16">
        <div className="bg-[#151A21] p-4 sm:p-6 md:p-8 border-b border-gray-800">
          <h1 className="text-2xl sm:text-4xl font-bold bg-gradient-to-r from-indigo-400 to-purple-400 text-transparent bg-clip-text">
            Active Oplaes
          </h1>
        </div>
        
        <div className="px-4 sm:px-6 md:px-8">
          <ViewToggle view={view} onViewChange={setView} />
          
          {view === 'grid' ? (
            <div className="space-y-8">
              <div>
                {processedCurrent.length > 0 && (
                  <div className="mb-8">
                    <Oplae 
                      key={processedCurrent[0].id} 
                      oplae={processedCurrent[0]} 
                      showOnlyInfluenceScore 
                    />
                  </div>
                )}

                <div className="flex">
                  {/* Current Oplaes */}
                  <div className="flex-1 -ml-6 pl-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {processedCurrent.map(oplae => (
                        <Oplae 
                          key={oplae.id} 
                          oplae={oplae} 
                          hideInfluenceScore 
                        />
                      ))}
                    </div>
                  </div>

                  {/* Vertical Separator */}
                  {processedUpcoming.length > 0 && (
                    <div className="mx-8 flex flex-col items-center">
                      <div className="h-full border-l border-dotted border-gray-300"></div>
                    </div>
                  )}

                  {/* Upcoming Oplaes */}
                  {processedUpcoming.length > 0 && (
                    <div className="flex-1 -mr-6 pr-6">
                      <h3 className="text-sm font-medium text-gray-500 mb-4 -ml-6">Next Month</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {processedUpcoming.map(oplae => (
                          <Oplae 
                            key={oplae.id} 
                            oplae={oplae} 
                            hideInfluenceScore 
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <h2 className="text-xl font-semibold mb-4 text-gray-400">Inactive</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
                  {processOplaes(data.inactive).map(oplae => (
                    <Oplae 
                      key={oplae.id} 
                      oplae={oplae}
                      onActivate={() => activateOplae(oplae.id)}
                      onDeactivate={() => deactivateOplae(oplae.id)}
                      onDelete={() => destroyOplae(oplae.id)}
                      isInactive 
                      hideInfluenceScore 
                    />
                  ))}
                </div>
              </div>

              {/* Vertical Separator */}
              {upcoming.length > 0 && (
                <div className="mx-8 flex flex-col items-center">
                  <div className="h-full border-l border-dotted border-gray-300"></div>
                </div>
              )}

              {/* Upcoming Oplaes */}
              {upcoming.length > 0 && (
                <div className="flex-1 -mr-6 pr-6">
                  <h3 className="text-sm font-medium text-gray-500 mb-4 -ml-6">Next Month</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {upcoming.map(oplae => (
                      <Oplae 
                        key={oplae.id} 
                        oplae={{
                          ...oplae,
                          videos: (oplae.videos || []).map(video => ({
                            ...video,
                            scheduled_date: video.posted_at,
                            colors: [oplae.color],
                            comments: [],
                            stats: {
                              comments: video.stats?.comments || 0,
                              shares: video.stats?.shares || 0,
                              likes: video.stats?.likes || 0
                            }
                          }))
                        }}
                        onActivate={() => activateOplae(oplae.id)}
                        onDeactivate={() => deactivateOplae(oplae.id)}
                        onDelete={() => destroyOplae(oplae.id)}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <FlowView oplaes={{ current: processedCurrent, upcoming: processedUpcoming }} />
          )}
        </div>
      </div>
    </div>
  );
};

export default Oplaes; 